'use client'

import { useEffect, useState, useCallback, useRef } from 'react'
import { useInView } from '@/hooks/useInView'

interface AnimatedCounterProps {
  /** Target number to count to */
  value: number
  /** Starting number (default: 0) */
  from?: number
  /** Prefix displayed before the number (e.g. '$') */
  prefix?: string
  /** Suffix displayed after the number (e.g. '%', '/mo') */
  suffix?: string
  /** Animation duration in milliseconds (default: 2000) */
  duration?: number
  /** Additional CSS class names */
  className?: string
  /** Intl.NumberFormat options for locale-aware formatting */
  formatOptions?: Intl.NumberFormatOptions
}

/** Quadratic ease-out: fast start, gentle deceleration */
function easeOutQuad(t: number): number {
  return 1 - (1 - t) * (1 - t)
}

export function AnimatedCounter({
  value,
  from = 0,
  prefix = '',
  suffix = '',
  duration = 2000,
  className,
  formatOptions,
}: AnimatedCounterProps) {
  const { ref, isInView } = useInView()
  // Start at the final value so server HTML (what non-JS crawlers read) shows the real number.
  const [displayValue, setDisplayValue] = useState(value)
  const hasAnimated = useRef(false)

  const formatNumber = useCallback(
    (n: number) => {
      const formatter = new Intl.NumberFormat(undefined, formatOptions)
      return formatter.format(Math.round(n))
    },
    [formatOptions]
  )

  useEffect(() => {
    if (!isInView || hasAnimated.current) return
    hasAnimated.current = true

    // Respect reduced motion
    if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) {
      setDisplayValue(value)
      return
    }

    let startTime: number | null = null
    let rafId: number

    function animate(timestamp: number) {
      if (startTime === null) startTime = timestamp

      const elapsed = timestamp - startTime
      const progress = Math.min(elapsed / duration, 1)
      const easedProgress = easeOutQuad(progress)

      const current = from + (value - from) * easedProgress
      setDisplayValue(current)

      if (progress < 1) {
        rafId = requestAnimationFrame(animate)
      }
    }

    rafId = requestAnimationFrame(animate)

    return () => {
      cancelAnimationFrame(rafId)
    }
  }, [isInView, value, from, duration])

  return (
    <span ref={ref} className={className} aria-label={`${prefix}${value}${suffix}`}>
      {prefix}
      {formatNumber(displayValue)}
      {suffix}
    </span>
  )
}
